import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import '../styles/ResultModal.css';
import '../styles/ProfileManagement.css';
import Header from '../components/Header';
import tuyenDungService from '../services/tuyenDungService';
import dotTuyenDungService from '../services/dotTuyenDungService';
import danhMucService from '../services/danhMucService';
import emailService from '../services/emailService';
import { transformApplicants } from '../utils/dataTransformers';
import { useNotification } from '../hooks/useNotification';
import Notification from '../components/Notification';
import ConfirmDialog from '../components/ConfirmDialog';

const ProfileManagement = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { notification, showSuccess, showError, hideNotification } = useNotification();

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedApplicants, setSelectedApplicants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [applicants, setApplicants] = useState([]);
  const [trangThaiList, setTrangThaiList] = useState([]);
  const [campaignMap, setCampaignMap] = useState({});
  const [dotList, setDotList] = useState([]);
  const [dotFilter, setDotFilter] = useState('all');

  const [showEmailModal, setShowEmailModal] = useState(false);
  const [ngayPhongVan, setNgayPhongVan] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(null);

  const [showHiredEmailModal, setShowHiredEmailModal] = useState(false);
  const [hiredEmailFields, setHiredEmailFields] = useState({
    thoiGianNhanViec: '',
    diaDiem: '',
    danhSachHoSo: '',
    thoiHanXacNhan: '',
  });

  useEffect(() => {
    if (user) {
      fetchData();
    }
  }, [user]);

  const fetchData = async () => {
    try {
      setLoading(true);

      const results = await Promise.allSettled([
        tuyenDungService.getAll(),
        dotTuyenDungService.getAll(),
        danhMucService.getTrangThaiTuyenDung(),
        danhMucService.getTonGiao(),
        danhMucService.getTinhTrangHonNhan(),
        danhMucService.getXepLoaiDaoTao()
      ]);

      const [applicantsRes, dotsRes, trangThaiRes, tonGiaosRes, tinhTrangHonNhansRes, xepLoaiDaoTaosRes] = results;

      if (applicantsRes.status !== 'fulfilled') {
        throw applicantsRes.reason;
      }
      const tonGiaos = tonGiaosRes.status === 'fulfilled' ? tonGiaosRes.value : [];
      const tinhTrangHonNhans = tinhTrangHonNhansRes.status === 'fulfilled' ? tinhTrangHonNhansRes.value : [];
      const xepLoaiDaoTaos = xepLoaiDaoTaosRes.status === 'fulfilled' ? xepLoaiDaoTaosRes.value : [];
      const dotsData = dotsRes.status === 'fulfilled' ? dotsRes.value : [];
      const dotsArr = Array.isArray(dotsData) ? dotsData : (dotsData?.data || []);
      const cMap = {};
      const dots = [];
      dotsArr.forEach(dot => {
        const dotId = dot.idDotTuyenDung ?? dot.id;
        const tenDot = dot.tenDotTuyenDung || `Đợt ${dotId}`;
        cMap[dotId] = tenDot;
        dots.push({ id: dotId, ten: tenDot });
      });
      setCampaignMap(cMap);
      setDotList(dots);


      const trangThaiRaw = trangThaiRes.status === 'fulfilled' ? trangThaiRes.value : null;
      const trangThaiArr = Array.isArray(trangThaiRaw) ? trangThaiRaw
        : Array.isArray(trangThaiRaw?.data) ? trangThaiRaw.data
          : [];

      const trangThaiTuyenDungs = trangThaiArr.map(t => ({
        idTrangThaiTuyenDung: t.idTrangThaiTuyenDung ?? t.id,
        tenTrangThaiTuyenDung: t.tenTrangThaiTuyenDung ?? t.ten
      }));
      setTrangThaiList(trangThaiTuyenDungs);

      const lookupData = { tonGiaos, tinhTrangHonNhans, trangThaiTuyenDungs, xepLoaiDaoTaos };
      const transformed = transformApplicants(applicantsRes.value, lookupData);
      const enriched = transformed.map(a => ({
        ...a,
        tenDotTuyenDung: cMap[a.dotTuyenDungId] || 'Chưa xác định'
      }));

      setApplicants(enriched);
      setError(null);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Không thể tải dữ liệu. Vui lòng thử lại sau.');
    } finally {
      setLoading(false);
    }
  };

  // Filtering
  const filteredApplicants = applicants.filter(applicant => {
    const matchSearch =
      applicant.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      applicant.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      applicant.position.toLowerCase().includes(searchTerm.toLowerCase());

    const matchStatus = !statusFilter ||
      applicant.idTrangThaiTuyenDung === Number(statusFilter);

    const matchDot = dotFilter === 'all' ||
      applicant.dotTuyenDungId === Number(dotFilter);

    return matchSearch && matchStatus && matchDot;
  });

  // Selection
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedApplicants(filteredApplicants.map(a => a.id));
    } else {
      setSelectedApplicants([]);
    }
  };

  const handleSelectOne = (id) => {
    if (selectedApplicants.includes(id)) {
      setSelectedApplicants(selectedApplicants.filter(a => a !== id));
    } else {
      setSelectedApplicants([...selectedApplicants, id]);
    }
  };

  // Status badge
  const getBadgeVariant = (statusId) => {
    switch (statusId) {
      case 1: return 'badge-secondary';
      case 2: return 'badge-warning';
      case 3: return 'badge-success';
      case 4: return 'badge-error';
      case 5: return 'badge-info';
      case 6: return 'badge-error';
      default: return 'badge-secondary';
    }
  };

  const getStatusBadge = (applicant) => {
    // Map status IDs to database values
    const statusId = applicant.idTrangThaiTuyenDung;

    switch (statusId) {
      case 1:
        return <span className="badge badge-secondary">Mới tiếp nhận</span>;
      case 2:
        return <span className="badge badge-warning">Đợi phỏng vấn</span>;
      case 3:
        return <span className="badge badge-success">Đạt phỏng vấn</span>;
      case 4:
        return <span className="badge badge-error">Không đạt phỏng vấn</span>;
      case 5:
        return <span className="badge badge-info">Đã tuyển dụng</span>;
      case 6:
        return <span className="badge badge-error">Không Đến Phỏng Vấn</span>;
      default:
        return <span className="badge badge-secondary">Không rõ</span>;
    }
  };

  // Stats - filtered by campaign if selected
  const statusDefinitions = [
    { id: 1, label: 'Mới tiếp nhận' },
    { id: 2, label: 'Đợi phỏng vấn' },
    { id: 3, label: 'Đạt phỏng vấn' },
    { id: 4, label: 'Không đạt phỏng vấn' },
    { id: 5, label: 'Đã tuyển dụng' },
    { id: 6, label: 'Không Đến Phỏng Vấn' }
  ];
 
  const applicantsForStats = dotFilter === 'all'
    ? applicants
    : applicants.filter(a => a.dotTuyenDungId === Number(dotFilter));

  const stats = {
    total: applicantsForStats.length,
    byStatus: statusDefinitions.map(status => ({
      id: status.id,
      label: status.label,
      count: applicantsForStats.filter(a => a.idTrangThaiTuyenDung === status.id).length
    }))
  };

  const getStatVariant = (statusId) => {
    switch (statusId) {
      case 1: return '';
      case 2: return 'warning';
      case 3: return 'success';
      case 4: return 'error';
      case 5: return 'info';
      case 6: return 'error';
      default: return '';
    }
  };

  // Email
  const handleOpenEmailModal = () => {
    setNgayPhongVan('');
    setShowEmailModal(true);
  };

  // Chi bao "da gui" khi backend xac nhan SMTP nhan gui thanh cong tung ung vien;
  // neu co ung vien that bai thi bao loi ro cho tung nguoi thay vi bao thanh cong chung chung.
  const reportEmailResult = (result, label) => {
    const data = result?.data;
    const failedCount = data?.failedCount ?? 0;
    const sentCount = data?.sentCount ?? selectedApplicants.length;

    if (!data || failedCount === 0) {
      showSuccess(`Đã gửi ${label} cho ${sentCount} ứng viên`);
      return;
    }

    const total = sentCount + failedCount;
    const details = (data.failed || [])
      .map(f => `${f.hoVaTen || f.email || ('ID ' + f.id)}: ${f.reason}`)
      .join('; ');
    showError(`Gửi thành công ${sentCount}/${total} ${label}. ${failedCount} ứng viên chưa nhận được mail — ${details}`);
  };

  const handleSendEmail = () => {
    if (!ngayPhongVan.trim() || selectedApplicants.length === 0) {
      showError('Vui lòng nhập ngày giờ phỏng vấn');
      return;
    }

    const [datePart, timePart] = ngayPhongVan.split('T');
    const [year, month, day] = datePart.split('-');
    const formatted = `${timePart} ${day}/${month}/${year}`;

    setConfirmDialog({
      message: `Xác nhận gửi lịch phỏng vấn (${formatted}) cho ${selectedApplicants.length} ứng viên đã chọn?`,
      onConfirm: async () => {
        setConfirmDialog(null);
        setSendingEmail(true);
        try {
          const result = await emailService.sendStatusNotification({
            applicantIds: selectedApplicants,
            ngayPhongVan: formatted
          });
          reportEmailResult(result, 'lịch phỏng vấn');
          setShowEmailModal(false);
          setSelectedApplicants([]);
          setNgayPhongVan('');
          
          fetchData();
        } catch (err) {
          const errorMsg = err.response?.data?.message || err.message || 'Không thể gửi mail';
          showError(errorMsg);
        } finally {
          setSendingEmail(false);
        }
      },
      onCancel: () => setConfirmDialog(null)
    });
  };

  const handleSendHiredEmail = () => {
    if (selectedApplicants.length === 0) {
      showError('Vui lòng chọn ít nhất một ứng viên');
      return;
    }

    if (process.env.REACT_APP_HIRED_EMAIL_FORM === 'true') {
      setHiredEmailFields({ thoiGianNhanViec: '', diaDiem: '', danhSachHoSo: '', thoiHanXacNhan: '' });
      setShowHiredEmailModal(true);
      return;
    }
 
    setConfirmDialog({
      message: `Xác nhận gửi email trúng tuyển cho ${selectedApplicants.length} ứng viên đã chọn?`,
      onConfirm: async () => {
        setConfirmDialog(null);
        setSendingEmail(true);
        try {
          const result = await emailService.sendHiredEmail({ applicantIds: selectedApplicants });
          reportEmailResult(result, 'email trúng tuyển');
          setSelectedApplicants([]);
          fetchData();
        } catch (err) {
          const errorMsg = err.response?.data?.message || err.message || 'Không thể gửi mail';
          showError(errorMsg);
        } finally {
          setSendingEmail(false);
        }
      },
      onCancel: () => setConfirmDialog(null)
    });
  };

  const handleConfirmSendHiredEmail = async () => {
    if (!hiredEmailFields.thoiGianNhanViec.trim() || !hiredEmailFields.diaDiem.trim() || !hiredEmailFields.thoiHanXacNhan.trim()) {
      showError('Vui lòng nhập đầy đủ thông tin bắt buộc');
      return;
    }
    setShowHiredEmailModal(false);
    setSendingEmail(true);
    try {
      const result = await emailService.sendHiredEmail({
        applicantIds: selectedApplicants,
        thoiGianNhanViec: hiredEmailFields.thoiGianNhanViec,
        diaDiem: hiredEmailFields.diaDiem,
        danhSachHoSo: hiredEmailFields.danhSachHoSo,
        thoiHanXacNhan: hiredEmailFields.thoiHanXacNhan,
      });
      reportEmailResult(result, 'email trúng tuyển');
      setSelectedApplicants([]);
      fetchData();
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || 'Không thể gửi mail';
      showError(errorMsg);
    } finally {
      setSendingEmail(false);
    }
  };


  const handleResetEmailStatus = (applicantId, applicantName) => {
    setConfirmDialog({
      message: `Xác nhận reset trạng thái email cho ứng viên "${applicantName}"?`,
      onConfirm: async () => {
        setConfirmDialog(null);
        try {
          await emailService.resetEmailStatus({
            applicantIds: [applicantId]
          });
          showSuccess('Đã reset trạng thái email');
    
          fetchData();
        } catch (err) {
          const errorMsg = err.response?.data?.message || err.message || 'Không thể reset trạng thái';
          showError(errorMsg);
        }
      },
      onCancel: () => setConfirmDialog(null)
    });
  };

  const handleResetHiredEmailStatus = (applicantId, applicantName) => {
    setConfirmDialog({
      message: `Xác nhận reset trạng thái email trúng tuyển cho ứng viên "${applicantName}"?`,
      onConfirm: async () => {
        setConfirmDialog(null);
        try {
          await emailService.resetHiredEmailStatus({
            applicantIds: [applicantId]
          });
          showSuccess('Đã reset trạng thái email trúng tuyển');
    
          fetchData();
        } catch (err) {
          const errorMsg = err.response?.data?.message || err.message || 'Không thể reset trạng thái';
          showError(errorMsg);
        }
      },
      onCancel: () => setConfirmDialog(null)
    });
  };
 
  const hasAlreadySentApplicants = selectedApplicants.some(id => {
    const applicant = applicants.find(a => a.id === id);
    return applicant?.daGuiEmailLichPV === true;
  });
 
  const hasAlreadySentHiredEmail = selectedApplicants.some(id => {
    const applicant = applicants.find(a => a.id === id);
    return applicant?.daGuiEmailTrungTuyen === true;
  });

   const hasAlreadySentFailedEmail = selectedApplicants.some(id => {
    const applicant = applicants.find(a => a.id === id);
    return applicant?.daGuiEmailRotPhongVan === true;
  });

  const handleSendFailedInterviewEmail = () => {
    if (selectedApplicants.length === 0) {
      showError('Vui lòng chọn ít nhất một ứng viên');
      return;
    }

    setConfirmDialog({
      message: `Xác nhận gửi email thông báo kết quả phỏng vấn (không đạt) cho ${selectedApplicants.length} ứng viên đã chọn?`,
      onConfirm: async () => {
        setConfirmDialog(null);
        setSendingEmail(true);
        try {
          const result = await emailService.sendFailedInterviewEmail({
            applicantIds: selectedApplicants
          });
          reportEmailResult(result, 'email kết quả phỏng vấn');
          setSelectedApplicants([]);
          fetchData();
        } catch (err) {
          const errorMsg = err.response?.data?.message || err.message || 'Không thể gửi mail';
          showError(errorMsg);
        } finally {
          setSendingEmail(false);
        }
      },
      onCancel: () => setConfirmDialog(null)
    });
  };

  const handleResetFailedEmailStatus = (applicantId, applicantName) => {
    setConfirmDialog({
      message: `Xác nhận reset trạng thái email kết quả phỏng vấn cho ứng viên "${applicantName}"?`,
      onConfirm: async () => {
        setConfirmDialog(null);
        try {
          await emailService.resetFailedInterviewEmailStatus({
            applicantIds: [applicantId]
          });
          showSuccess('Đã reset trạng thái email');
          fetchData();
        } catch (err) {
          const errorMsg = err.response?.data?.message || err.message || 'Không thể reset trạng thái';
          showError(errorMsg);
        }
      },
      onCancel: () => setConfirmDialog(null)
    });
  };

  if (loading) {
    return (
      <div className="page-wrapper">
        <div className="page-container">
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
            <p>Đang tải dữ liệu...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-wrapper">
        <div className="page-container">
          <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', gap: '1rem' }}>
            <p style={{ color: '#dc2626' }}>{error}</p>
            <button onClick={fetchData} className="btn btn-primary">Thử lại</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-wrapper profile-management">

      <div className="app-background">
        <div className="floating-icons">
          <div className="floating-icon">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 3c1.93 0 3.5 1.57 3.5 3.5S13.93 13 12 13s-3.5-1.57-3.5-3.5S10.07 6 12 6zm7 13H5v-.23c0-.62.28-1.2.76-1.58C7.47 15.82 9.64 15 12 15s4.53.82 6.24 2.19c.48.38.76.97.76 1.58V19z" />
            </svg>
          </div>
          <div className="floating-icon">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
            </svg>
          </div>
        </div>
      </div>

      <div className="page-container">

        <Header title="Quản Lý Hồ Sơ" />

        <main className="page-main">

          <div className="breadcrumb">
            <button onClick={() => navigate('/trangchu')} className="breadcrumb-link">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                <polyline points="9 22 9 12 15 12 15 22" />
              </svg>
              Trang Chủ
            </button>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
              <polyline points="9 18 15 12 9 6" />
            </svg>
            <span className="breadcrumb-current">Quản Lý Hồ Sơ</span>
          </div>
 
          <div className="stats-row">
            <div className="stat-mini-card">
              <span className="stat-mini-number">{stats.total}</span>
              <span className="stat-mini-label">Tổng hồ sơ</span>
            </div>
            {stats.byStatus.map(s => (
              <div key={s.id} className={`stat-mini-card ${getStatVariant(s.id)}`}>
                <span className="stat-mini-number">{s.count}</span>
                <span className="stat-mini-label">{s.label}</span>
              </div>
            ))}
          </div>
 
          <div className="applicants-section">
            <div className="section-header">
              <h3>Danh sách ứng viên</h3>
              <div className="section-actions">
                {selectedApplicants.length > 0 && (
                  <span className="selected-count">{selectedApplicants.length} đã chọn</span>
                )}
                <button onClick={fetchData} className="btn btn-secondary" disabled={loading} title="Tải lại dữ liệu từ server">
                  {loading ? 'Đang tải...' : 'Làm mới'}
                </button>
              </div>
            </div>

            <div className="pm-filters-bar">
              <div className="pm-search-box">
                <svg className="pm-search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
                <input
                  className="pm-search-input"
                  type="text"
                  placeholder="Tìm kiếm theo tên, email, vị trí..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
 
              <div className="pm-campaign-filter">
                <label htmlFor="dotFilter" className="pm-campaign-label">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                    <line x1="16" y1="2" x2="16" y2="6" />
                    <line x1="8" y1="2" x2="8" y2="6" />
                    <line x1="3" y1="10" x2="21" y2="10" />
                  </svg>
                  Đợt tuyển dụng:
                </label>
                <select
                  id="dotFilter"
                  className="pm-campaign-select"
                  value={dotFilter}
                  onChange={(e) => setDotFilter(e.target.value)}
                >
                  <option value="all">Tất cả đợt ({applicants.length})</option>
                  {dotList.map(dot => (
                    <option key={dot.id} value={String(dot.id)}>
                      {dot.ten} ({applicants.filter(a => a.dotTuyenDungId === dot.id).length})
                    </option>
                  ))}
                </select>
              </div> 
              <div className="pm-filter-tabs">
                <button
                  className={`pm-filter-tab ${!statusFilter ? 'active' : ''}`}
                  onClick={() => setStatusFilter('')}
                >
                  Tất cả ({applicantsForStats.length})
                </button>
                {statusDefinitions.map(status => (
                  <button
                    key={status.id}
                    className={`pm-filter-tab ${statusFilter === String(status.id) ? 'active' : ''}`}
                    onClick={() => setStatusFilter(String(status.id))}
                  >
                    {status.label} ({applicantsForStats.filter(a => a.idTrangThaiTuyenDung === status.id).length})
                  </button>
                ))}
              </div>
            </div>

            <div className="table-container">
              <table className="applicants-table profile-table">
                <thead>
                  <tr>
                    <th className="th-checkbox">
                      <input
                        type="checkbox"
                        onChange={handleSelectAll}
                        checked={filteredApplicants.length > 0 && selectedApplicants.length === filteredApplicants.length}
                      />
                    </th>
                    <th>Ứng viên</th>
                    <th className="hide-mobile">Email</th>
                    <th className="hide-mobile">Vị trí ứng tuyển</th>
                    <th className="hide-tablet">Ngày nộp</th>
                    <th className="hide-mobile">Đợt phỏng vấn</th>
                    <th>Trạng thái</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredApplicants.map(applicant => (
                    <tr
                      key={applicant.id}
                      className={`clickable-row ${selectedApplicants.includes(applicant.id) ? 'selected' : ''} ${(applicant.daGuiEmailLichPV || applicant.daGuiEmailTrungTuyen) ? 'email-sent' : ''}`}
                      onClick={() => handleSelectOne(applicant.id)}
                    >
                      <td className="td-checkbox" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedApplicants.includes(applicant.id)}
                          onChange={() => handleSelectOne(applicant.id)}
                        />
                      </td>
                      <td>
                        <div className="applicant-info">
                          <div className="applicant-avatar">
                            {applicant.avatar ? (
                              <img src={applicant.avatar} alt={applicant.name} />
                            ) : (
                              applicant.name.charAt(0)
                            )}
                          </div>
                          <div className="applicant-details">
                            <span className="applicant-name">{applicant.name}</span>
                            <span className="applicant-phone hide-desktop">{applicant.phone}</span>
                          </div>
                        </div>
                      </td>
                      <td className="hide-mobile" data-label="Email">
                        <span className="email-text">{applicant.email}</span>
                      </td>
                      <td className="hide-mobile" data-label="Vị trí">
                        <span className="position-text">{applicant.position}</span>
                      </td>
                      <td className="hide-tablet" data-label="Ngày nộp">
                        <span className="date-text">{applicant.appliedDate}</span>
                      </td>
                      <td className="hide-mobile" data-label="Đợt PV">
                        <span className="campaign-tag">{applicant.tenDotTuyenDung}</span>
                      </td>
                      <td data-label="Trạng thái">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {getStatusBadge(applicant)}
                          {applicant.daGuiEmailLichPV && (
                            <button
                              className="btn-reset-email"
                              title="Reset trạng thái gửi email"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleResetEmailStatus(applicant.id, applicant.name);
                              }}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '28px',
                                height: '28px',
                                padding: '6px',
                                border: 'none',
                                borderRadius: '6px',
                                background: '#f97316',
                                color: 'white',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease'
                              }}
                              onMouseOver={(e) => e.currentTarget.style.background = '#ea580c'}
                              onMouseOut={(e) => e.currentTarget.style.background = '#f97316'}
                            >
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="16" height="16">
                                <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
                                <path d="M21 3v5h-5" />
                                <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
                                <path d="M3 21v-5h5" />
                              </svg>
                            </button>
                          )}
                          {applicant.daGuiEmailTrungTuyen && (
                            <button
                              className="btn-reset-email"
                              title="Reset trạng thái gửi email trúng tuyển"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleResetHiredEmailStatus(applicant.id, applicant.name);
                              }}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '28px',
                                height: '28px',
                                padding: '6px',
                                border: 'none',
                                borderRadius: '6px',
                                background: '#10b981',
                                color: 'white',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease'
                              }}
                              onMouseOver={(e) => e.currentTarget.style.background = '#059669'}
                              onMouseOut={(e) => e.currentTarget.style.background = '#10b981'}
                            >
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="16" height="16">
                                <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
                                <path d="M21 3v5h-5" />
                                <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
                                <path d="M3 21v-5h5" />
                              </svg>
                            </button>
                          )}
                          {applicant.daGuiEmailRotPhongVan && (
                            <button
                              className="btn-reset-email"
                              title="Reset trạng thái gửi email kết quả phỏng vấn"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleResetFailedEmailStatus(applicant.id, applicant.name);
                              }}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '28px',
                                height: '28px',
                                padding: '6px',
                                border: 'none',
                                borderRadius: '6px',
                                background: '#ef4444',
                                color: 'white',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease'
                              }}
                              onMouseOver={(e) => e.currentTarget.style.background = '#dc2626'}
                              onMouseOut={(e) => e.currentTarget.style.background = '#ef4444'}
                            >
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="16" height="16">
                                <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
                                <path d="M21 3v5h-5" />
                                <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
                                <path d="M3 21v-5h5" />
                              </svg>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {filteredApplicants.length === 0 && (
                <div className="empty-state">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="48" height="48">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                  <p>Không tìm thấy ứng viên nào</p>
                </div>
              )}
            </div>
          </div>
        </main>

        <footer className="page-footer">
          <p>&copy; Copyright 2026 {process.env.REACT_APP_FOOTER_NAME || 'Bệnh Viện Hùng Vương Gia Lai'}</p>
          <p>Powered Ban Chuyển Đổi Số - leanhquan™</p>
        </footer>
      </div>
      {selectedApplicants.length > 0 && (
        <div className="floating-action">
          {!hasAlreadySentApplicants && (
            <button className="btn btn-primary" onClick={handleOpenEmailModal} style={{ marginRight: '12px' }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
              Gửi Mail LPV ({selectedApplicants.length})
            </button>
          )}
          {!hasAlreadySentHiredEmail && (
            <button className="btn btn-success" onClick={handleSendHiredEmail}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
              Gửi Mail TT ({selectedApplicants.length})
            </button>
          )}
          {!hasAlreadySentFailedEmail && (
            <button className="btn btn-error" onClick={handleSendFailedInterviewEmail} style={{ marginLeft: '12px' }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <line x1="15" y1="9" x2="9" y2="15" />
                <line x1="9" y1="9" x2="15" y2="15" />
              </svg>
              Gửi Mail RTP ({selectedApplicants.length})
            </button>
          )}
        </div>
      )}
 
      {showEmailModal && (
        <div className="modal-overlay email-modal" onClick={() => setShowEmailModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Gửi Lịch Phỏng Vấn</h2>
              <button className="modal-close" onClick={() => setShowEmailModal(false)}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div className="modal-body">
              <p className="email-modal-description">
                Nhập ngày giờ phỏng vấn để gửi thông báo cho <strong>{selectedApplicants.length}</strong> ứng viên đã chọn:
              </p>
              <div className="email-modal-input-group">
                <label htmlFor="ngayPhongVan" className="email-modal-label">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                    <line x1="16" y1="2" x2="16" y2="6" />
                    <line x1="8" y1="2" x2="8" y2="6" />
                    <line x1="3" y1="10" x2="21" y2="10" />
                  </svg>
                  Ngày giờ phỏng vấn
                </label>
                <input
                  id="ngayPhongVan"
                  type="datetime-local"
                  className="email-modal-input"
                  value={ngayPhongVan}
                  onChange={(e) => setNgayPhongVan(e.target.value)}
                  autoFocus
                />
              </div>
              <div className="email-modal-actions">
                <button className="btn btn-cancel" onClick={() => setShowEmailModal(false)}>
                  Hủy
                </button>
                <button
                  className="btn btn-send"
                  disabled={!ngayPhongVan.trim() || sendingEmail}
                  onClick={handleSendEmail}
                >
                  {sendingEmail ? (
                    'Đang gửi...'
                  ) : (
                    <>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="22" y1="2" x2="11" y2="13" />
                        <polygon points="22 2 15 22 11 13 2 9 22 2" />
                      </svg>
                      Gửi lịch phỏng vấn
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
 
      {showHiredEmailModal && (
        <div className="modal-overlay email-modal" onClick={() => setShowHiredEmailModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Gửi Mail Thông Báo Trúng Tuyển</h2>
              <button className="modal-close" onClick={() => setShowHiredEmailModal(false)}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div className="modal-body">
              <p style={{ marginTop: 0 }}>
                Nhập thông tin tiếp nhận công việc để gửi thông báo cho <strong>{selectedApplicants.length}</strong> ứng viên đã chọn:
              </p>
              <div className="email-modal-input-group">
                <label className="email-modal-label">Thời gian nhận việc <span style={{ color: '#e53e3e' }}>*</span></label>
                <input
                  type="text"
                  className="email-modal-input"
                  placeholder="VD: 7h30, ngày 20/05/2025"
                  value={hiredEmailFields.thoiGianNhanViec}
                  onChange={(e) => setHiredEmailFields(f => ({ ...f, thoiGianNhanViec: e.target.value }))}
                  autoFocus
                />
              </div>
              <div className="email-modal-input-group">
                <label className="email-modal-label">Địa điểm <span style={{ color: '#e53e3e' }}>*</span></label>
                <input
                  type="text"
                  className="email-modal-input"
                  placeholder="VD: Phòng Kế hoạch Tổng hợp, tầng 1 – Nhà A"
                  value={hiredEmailFields.diaDiem}
                  onChange={(e) => setHiredEmailFields(f => ({ ...f, diaDiem: e.target.value }))}
                />
              </div>
              <div className="email-modal-input-group">
                <label className="email-modal-label">Hồ sơ cần bổ sung (nếu có)</label>
                <input
                  type="text"
                  className="email-modal-input"
                  placeholder="VD: Bản sao bằng tốt nghiệp, ảnh 4x6..."
                  value={hiredEmailFields.danhSachHoSo}
                  onChange={(e) => setHiredEmailFields(f => ({ ...f, danhSachHoSo: e.target.value }))}
                />
              </div>
              <div className="email-modal-input-group">
                <label className="email-modal-label">Thời hạn xác nhận <span style={{ color: '#e53e3e' }}>*</span></label>
                <input
                  type="text"
                  className="email-modal-input"
                  placeholder="VD: 18/05/2025"
                  value={hiredEmailFields.thoiHanXacNhan}
                  onChange={(e) => setHiredEmailFields(f => ({ ...f, thoiHanXacNhan: e.target.value }))}
                />
              </div>
              <div className="email-modal-actions">
                <button className="btn btn-cancel" onClick={() => setShowHiredEmailModal(false)}>
                  Hủy
                </button>
                <button
                  className="btn btn-send"
                  disabled={!hiredEmailFields.thoiGianNhanViec.trim() || !hiredEmailFields.diaDiem.trim() || !hiredEmailFields.thoiHanXacNhan.trim() || sendingEmail}
                  onClick={handleConfirmSendHiredEmail}
                >
                  {sendingEmail ? (
                    'Đang gửi...'
                  ) : (
                    <>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="22" y1="2" x2="11" y2="13" />
                        <polygon points="22 2 15 22 11 13 2 9 22 2" />
                      </svg>
                      Gửi mail trúng tuyển
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={hideNotification}
          duration={notification.duration}
        />
      )}

      {confirmDialog && (
        <ConfirmDialog
          message={confirmDialog.message}
          onConfirm={confirmDialog.onConfirm}
          onCancel={confirmDialog.onCancel}
        />
      )}
    </div>
  );
};

export default ProfileManagement;
